import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { AxiosError } from 'axios';

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

/**
 * Get the current user's auth token
 */
async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  if (!token) {
    throw new Error('No authentication token available');
  }

  return token;
}

/**
 * Create axios instance with auth header
 */
async function createAuthenticatedClient() {
  const token = await getAuthToken();

  return axios.create({
    baseURL: API_ENDPOINT,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

// ============================================
// Types
// ============================================

export interface WholesaleUser {
  userId: string;
  email: string;
  name: string;
  companyName: string;
  taxId: string;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  enabled: boolean;
  createdAt: string;
  status: string;
}

export interface UserStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export interface ListUsersResponse {
  success: boolean;
  users: WholesaleUser[];
  stats: UserStats;
}

export interface UpdateUserResponse {
  success: boolean;
  message: string;
  user: {
    userId: string;
    email: string;
    name: string;
    companyName: string;
    approvalStatus: string;
    enabled: boolean;
  };
}

export interface DraftOrderLineItem {
  title: string;
  quantity: number;
  originalPrice: string;
  discountedPrice: string;
  sku: string;
}

export interface DraftOrder {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  status: 'OPEN' | 'INVOICE_SENT' | 'COMPLETED';
  totalPrice: string;
  currency: string;
  customer: {
    id: string;
    email: string;
    name: string;
  } | null;
  lineItems: DraftOrderLineItem[];
  tags: string[];
  note: string | null;
  invoiceUrl: string | null;
  invoiceSentAt: string | null;
}

export interface OrderStats {
  total: number;
  open: number;
  invoiceSent: number;
  completed: number;
}

export interface ListOrdersResponse {
  success: boolean;
  orders: DraftOrder[];
  stats: OrderStats;
  hasMore: boolean;
}

export interface ApiError {
  error: string;
  message: string;
}

// ============================================
// Settings Types
// ============================================

export interface GlobalSettings {
  wholesaleDiscountPercent: number;
  quantityDiscountThreshold: number;
  quantityDiscountAmount: number;
  minimumOrderValue: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ProductSettings {
  productId: string;
  productTitle?: string;
  hidden: boolean;
  wholesaleDiscountPercent?: number;
  quantityDiscountThreshold?: number;
  quantityDiscountAmount?: number;
  quantityDiscountEnabled?: boolean;
  minimumQuantity?: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface GetSettingsResponse {
  success: boolean;
  settings: GlobalSettings;
}

export interface UpdateSettingsResponse {
  success: boolean;
  message: string;
  settings: GlobalSettings;
}

export interface GetProductSettingsResponse {
  success: boolean;
  productSettings: ProductSettings[];
  count: number;
}

export interface UpdateProductSettingsResponse {
  success: boolean;
  message: string;
  productSettings: ProductSettings;
}

// ============================================
// Display Case Types
// ============================================

export interface DisplayCase {
  caseId: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  dimensions: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface DisplayCasePurchase {
  cognitoSub: string;
  caseId: string;
  customerEmail: string;
  companyName: string;
  caseName: string;
  purchasePrice: number;
  status: 'requested' | 'approved' | 'active' | 'completed' | 'cancelled';
  totalRebateEarned: number;
  remainingBalance: number;
  requestedAt: string;
  updatedAt: string;
  rebates?: DisplayCaseRebate[];
}

export interface DisplayCaseRebate {
  cognitoSub: string;
  purchaseSk: string;
  orderId: string;
  orderName: string;
  orderTotal: number;
  rebatePercent: number;
  rebateAmount: number;
  cappedAmount: number;
  createdAt: string;
}

export interface DisplayCaseProgramSettings {
  active: boolean;
  rebatePercent: number;
  rebateDescription: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  productType: string;
  tags: string[];
  imageUrl: string | null;
  variants: Array<{
    id: string;
    title: string;
    sku: string;
    price: string;
    wholesalePrice: string;
    inventoryQuantity: number;
  }>;
  settings?: {
    wholesaleDiscountPercent: number;
    quantityDiscountThreshold: number;
    quantityDiscountAmount: number;
    quantityDiscountEnabled: boolean;
    minimumQuantity: number;
    hasCustomSettings: boolean;
  };
}

// ============================================
// Admin API Functions
// ============================================

/**
 * Fetch all wholesale users (admin only)
 */
export async function listUsers(): Promise<ListUsersResponse> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.get<ListUsersResponse>('/admin/users');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch users');
    }
    throw error;
  }
}

/**
 * Update user status (approve, reject, disable, enable)
 */
export async function updateUserStatus(
  userId: string,
  action: 'approve' | 'reject' | 'disable' | 'enable'
): Promise<UpdateUserResponse> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.put<UpdateUserResponse>(
      `/admin/users/${userId}/${action}`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Admin access required');
      }
      if (axiosError.response?.status === 404) {
        throw new Error('User not found');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to update user');
    }
    throw error;
  }
}

/**
 * Fetch draft orders from Shopify (admin only)
 */
export async function listOrders(params?: {
  status?: 'open' | 'invoice_sent' | 'completed';
  limit?: number;
}): Promise<ListOrdersResponse> {
  try {
    const client = await createAuthenticatedClient();
    const queryParams = new URLSearchParams();

    if (params?.status) {
      queryParams.append('status', params.status);
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const queryString = queryParams.toString();
    const url = `/admin/orders${queryString ? `?${queryString}` : ''}`;

    const response = await client.get<ListOrdersResponse>(url);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch orders');
    }
    throw error;
  }
}

// ============================================
// Settings API Functions
// ============================================

/**
 * Fetch global wholesale settings (admin only)
 */
export async function getSettings(): Promise<GetSettingsResponse> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.get<GetSettingsResponse>('/admin/settings');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch settings');
    }
    throw error;
  }
}

/**
 * Update global wholesale settings (admin only)
 */
export async function updateSettings(
  settings: Partial<GlobalSettings>
): Promise<UpdateSettingsResponse> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.put<UpdateSettingsResponse>('/admin/settings', settings);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to update settings');
    }
    throw error;
  }
}

/**
 * Fetch all product-specific settings (admin only)
 */
export async function getProductSettings(): Promise<GetProductSettingsResponse> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.get<GetProductSettingsResponse>('/admin/products/settings');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch product settings');
    }
    throw error;
  }
}

/**
 * Update settings for a specific product (admin only)
 */
// URL-safe Base64 encode (replaces + with - and / with _)
function base64UrlEncode(str: string): string {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function updateProductSetting(
  productId: string,
  settings: Partial<ProductSettings>
): Promise<UpdateProductSettingsResponse> {
  try {
    const client = await createAuthenticatedClient();
    // Use URL-safe Base64 encoding to avoid issues with slashes in gid://shopify/Product/...
    const encodedProductId = base64UrlEncode(productId);
    const response = await client.put<UpdateProductSettingsResponse>(
      `/admin/products/${encodedProductId}/settings`,
      settings
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to update product settings');
    }
    throw error;
  }
}

/**
 * Delete product-specific settings (revert to global defaults)
 */
export async function deleteProductSetting(productId: string): Promise<{ success: boolean; message: string }> {
  try {
    const client = await createAuthenticatedClient();
    // Use URL-safe Base64 encoding to avoid issues with slashes in gid://shopify/Product/...
    const encodedProductId = base64UrlEncode(productId);
    const response = await client.delete<{ success: boolean; message: string }>(
      `/admin/products/${encodedProductId}/settings`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) {
        throw new Error('Admin access required');
      }
      throw new Error(axiosError.response?.data?.message || 'Failed to delete product settings');
    }
    throw error;
  }
}

// ============================================
// Display Case Admin API Functions
// ============================================

export async function listDisplayCases(): Promise<{ success: boolean; cases: DisplayCase[] }> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.get('/admin/display-cases');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Admin access required');
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch display cases');
    }
    throw error;
  }
}

export async function createDisplayCase(
  data: Omit<DisplayCase, 'caseId' | 'createdAt' | 'updatedAt'>
): Promise<{ success: boolean; displayCase: DisplayCase }> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.post('/admin/display-cases', data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Admin access required');
      throw new Error(axiosError.response?.data?.message || 'Failed to create display case');
    }
    throw error;
  }
}

export async function updateDisplayCase(
  caseId: string,
  data: Partial<DisplayCase>
): Promise<{ success: boolean; displayCase: DisplayCase }> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.put(`/admin/display-cases/${caseId}`, data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Admin access required');
      throw new Error(axiosError.response?.data?.message || 'Failed to update display case');
    }
    throw error;
  }
}

export async function deleteDisplayCase(caseId: string): Promise<{ success: boolean; message: string }> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.delete(`/admin/display-cases/${caseId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Admin access required');
      throw new Error(axiosError.response?.data?.message || 'Failed to delete display case');
    }
    throw error;
  }
}

export async function listDisplayCasePurchases(): Promise<{ success: boolean; purchases: DisplayCasePurchase[] }> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.get('/admin/display-cases/purchases');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Admin access required');
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch purchases');
    }
    throw error;
  }
}

export async function updateDisplayCasePurchaseStatus(
  purchaseSk: string,
  action: 'approve' | 'activate' | 'cancel'
): Promise<{ success: boolean; message: string; purchase: DisplayCasePurchase }> {
  try {
    const client = await createAuthenticatedClient();
    const encoded = encodeURIComponent(purchaseSk);
    const response = await client.put(`/admin/display-cases/purchases/${encoded}/${action}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Admin access required');
      throw new Error(axiosError.response?.data?.message || 'Failed to update purchase');
    }
    throw error;
  }
}

export async function getDisplayCaseSettings(): Promise<{
  success: boolean;
  settings: DisplayCaseProgramSettings;
}> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.get('/admin/display-cases/settings');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Admin access required');
      throw new Error(axiosError.response?.data?.message || 'Failed to fetch display case settings');
    }
    throw error;
  }
}

export async function updateDisplayCaseSettings(
  settings: Partial<DisplayCaseProgramSettings>
): Promise<{ success: boolean; message: string; settings: DisplayCaseProgramSettings }> {
  try {
    const client = await createAuthenticatedClient();
    const response = await client.put('/admin/display-cases/settings', settings);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 403) throw new Error('Admin access required');
      throw new Error(axiosError.response?.data?.message || 'Failed to update display case settings');
    }
    throw error;
  }
}
